import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { CreateTaskDto } from '@/types/tasks'

/** Dados para pré-preencher a gaveta ao criar tarefa a partir de outra tela. */
export type TaskPrefill = Partial<CreateTaskDto> & {
  /** Rótulo do vínculo mostrado na gaveta (ex.: "Publicação 0001234-56…"). */
  origemLabel?: string
}

interface TaskDrawerApi {
  /** Id aberto na gaveta, 'nova' ou null. */
  current: number | 'nova' | null
  prefill: TaskPrefill | null
  openTask: (id: number) => void
  newTask: (prefill?: TaskPrefill, onCreated?: () => void) => void
  close: () => void
  /** Sobe a cada gravação feita pela gaveta (listas recarregam). */
  version: number
  notifyChanged: (createdId?: number) => void
}

const TaskDrawerContext = createContext<TaskDrawerApi | null>(null)

export const DRAWER_PARAM = 'tarefa'

/**
 * Gaveta única de tarefa: o estado aberto fica na URL (`?tarefa=<id>` ou
 * `?tarefa=nova`), preservando a tela e os filtros onde ela foi aberta.
 */
export function TaskDrawerProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [prefill, setPrefill] = useState<TaskPrefill | null>(null)
  const [version, setVersion] = useState(0)
  const onCreatedRef = useRef<(() => void) | null>(null)

  const raw = searchParams.get(DRAWER_PARAM)
  const current: TaskDrawerApi['current'] = raw === 'nova' ? 'nova' : raw && /^\d+$/.test(raw) ? Number(raw) : null

  const setParam = useCallback((value: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value) next.set(DRAWER_PARAM, value)
      else next.delete(DRAWER_PARAM)
      return next
    })
  }, [setSearchParams])

  const openTask = useCallback((id: number) => setParam(String(id)), [setParam])

  const newTask = useCallback((data?: TaskPrefill, onCreated?: () => void) => {
    setPrefill(data ?? null)
    onCreatedRef.current = onCreated ?? null
    setParam('nova')
  }, [setParam])

  const close = useCallback(() => {
    setPrefill(null)
    setParam(null)
  }, [setParam])

  const notifyChanged = useCallback((createdId?: number) => {
    setVersion((v) => v + 1)
    if (createdId !== undefined) {
      onCreatedRef.current?.()
      onCreatedRef.current = null
      setPrefill(null)
      setParam(String(createdId))
    }
  }, [setParam])

  const value = useMemo<TaskDrawerApi>(
    () => ({ current, prefill, openTask, newTask, close, version, notifyChanged }),
    [current, prefill, openTask, newTask, close, version, notifyChanged]
  )
  return <TaskDrawerContext.Provider value={value}>{children}</TaskDrawerContext.Provider>
}

export function useTaskDrawer(): TaskDrawerApi {
  const api = useContext(TaskDrawerContext)
  if (!api) throw new Error('useTaskDrawer precisa do TaskDrawerProvider')
  return api
}
